import * as React from 'react';

import { ReportTypes } from '@showbie-socrative/socrative-utils';
import { sendDownloadInitiateMessage } from '~showbie/messages/sendDownloadMessage';

import { ExportAnswerKeyEventAdapter } from '~analytics/adapters/ExportAnswerKeyEventAdapter';
import { translate } from '~l10n/client';
import { Divider } from '~shared/components/Divider';
import { IconDownload } from '~shared/components/icons/standard';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { PaywallProvider } from '~teacher/components/PaywallDialog';

import { downloadAnswerKey } from '../../../../api/quiz/downloadQuiz/downloadQuiz';
import { pollForDownloadReady } from '../../../../util/ReportDownload';
import { ExportFeedbackRetryError } from '../../ExportReports/ExportFeedback/ExportFeedback';
import {
  AssessmentsExportFeedbackDownloadInProgress,
  ExportFeedbackDownloadComplete,
} from '../../ExportReports/ExportFeedback/ExportFeedbackDownload';
import { IncludeExplanations } from '../../ExportReports/ReportOption';
import { ReportOptionButton } from '../../ExportReports/ReportOption/ReportOptionButton';
import { ReportOptionControlList } from '../../ExportReports/ReportOption/ReportOptionControlList';
import { ReportOptionHeader } from '../../ExportReports/ReportOption/ReportOptionHeader';
import {
  toDownloading,
  toError,
  toPreparing,
  useReportReducer,
} from '../../ExportReports/ReportOption/ReportOptionHook';
import {
  ExportView,
  beginAsync,
  completeAsync,
  toForm,
} from '../../ExportReports/ReportOption/ReportOptionHookReducer';
import { ReportTypeContext } from '../../ExportReports/ReportTypeContext';

interface QuizAnswerKeyExportModalProps {
  quizId: number | string; // Can be number for regular quizzes or string (GUID) for workspace quizzes
  workspaceId?: string;
  onExportingChange?: (exporting: boolean) => void;
}

export function QuizAnswerKeyExportModal({
  quizId,
  workspaceId,
  onExportingChange,
}: QuizAnswerKeyExportModalProps) {
  const reportOptions = [IncludeExplanations];
  const { dispatch, state } = useReportReducer({
    reportOptions,
    initialOptions: {
      include_explanations: false,
      include_correct_answers: true,
    },
  });

  /*
  Send analytics when modal opens
  Using an empty dependency array to only run on mount
  */
  React.useEffect(() => {
    new ExportAnswerKeyEventAdapter({
      library: workspaceId ? 'shared' : 'personal',
      action: 'open',
      source: 'quiz_menu',
    }).sendAnalytics();
  }, [workspaceId]);
  const appContext = useTeacherApplicationContext();
  const { exportView, optionState, error, isFetching, preparedUrl } = state;
  const onBackToOptionSettings = () => toForm(dispatch)();

  // Inform parent whenever we enter/exit the export flow
  React.useEffect(() => {
    onExportingChange?.(exportView !== ExportView.FORM);
  }, [exportView, onExportingChange]);

  const handleDownload = React.useCallback(() => {
    if (!preparedUrl) {
      console.error('No download URL available');
      return;
    }

    // For Showbie Assessments, send Download.Initiate PostMessage
    if (appContext === TeacherApplicationContext.ShowbieAssessments) {
      sendDownloadInitiateMessage(preparedUrl);
    }
  }, [preparedUrl, appContext]);

  // Automatically trigger download when file is ready
  React.useEffect(() => {
    if (exportView === ExportView.DOWNLOADING && preparedUrl) {
      handleDownload();
    }
  }, [exportView, preparedUrl, handleDownload]);

  const handlePrepareDownload = React.useCallback(async () => {
    try {
      // Set to preparing state
      beginAsync(dispatch)();
      toPreparing(dispatch)('');

      // Check to see if it's a workspace quiz and using the appropriate API
      let response;
      if (workspaceId) {
        const { downloadWorkspaceAnswerKey } = await import(
          '../../../../api/quiz/downloadQuiz'
        );
        // For workspace quizzes, quizId is a string (GUID)
        response = await downloadWorkspaceAnswerKey(
          quizId as string,
          workspaceId,
          optionState
        );
      } else {
        // For regular quizzes (quizId is a number)
        response = await downloadAnswerKey(quizId as number, optionState);
      }

      const pollingUrl = response.url;

      // Update with polling URL
      toPreparing(dispatch)(pollingUrl);

      // Poll for the download to be ready
      // Pass ShowbieAssessments context to prevent auto-download in pollForDownloadReady
      // We handle the download ourselves in the modal
      pollForDownloadReady({
        pollingUrl,
        onPollComplete: (downloadUrl: string) => {
          toDownloading(dispatch)(downloadUrl);

          // Track successful download
          new ExportAnswerKeyEventAdapter({
            library: workspaceId ? 'shared' : 'personal',
            action: 'click',
            source: 'export_button',
          }).sendAnalytics();
        },
        onPollError: (error: any) => {
          toError(dispatch)(error);
        },
        appContext,
      });

      completeAsync(dispatch)();
    } catch (e) {
      toError(dispatch)(e);
    }
  }, [quizId, workspaceId, optionState, dispatch, appContext]);

  const renderContent = () => {
    switch (exportView) {
      case ExportView.FORM:
        return (
          <ReportTypeContext.Provider value={ReportTypes.EmptyQuiz}>
            <section className="flex flex-col justify-between h-full report-options">
              <ReportOptionHeader
                description={translate(
                  'Export a PDF of the correct quiz answers.'
                )}
                shouldShowProBadge={false}
                shouldHaveSpaceBeforeTitle={false}
              />
              <div className="pt-8">
                <Divider />
                <ReportOptionControlList
                  shouldShowProBadge={false}
                  options={reportOptions}
                  dispatch={dispatch}
                  optionState={optionState}
                />
                <Divider />
              </div>
              <div className="mt-15">
                <div className="flex justify-end">
                  <div>
                    <ReportOptionButton
                      icon={<IconDownload />}
                      onClick={handlePrepareDownload}
                      isDisabled={isFetching}
                    >
                      {translate('Export')}
                    </ReportOptionButton>
                  </div>
                </div>
              </div>
            </section>
          </ReportTypeContext.Provider>
        );
      case ExportView.PREPARING:
        return (
          <AssessmentsExportFeedbackDownloadInProgress
            onCancel={onBackToOptionSettings}
          />
        );
      case ExportView.DOWNLOADING:
        return <ExportFeedbackDownloadComplete />;
      case ExportView.ERROR:
        return (
          <ExportFeedbackRetryError
            error={error}
            onRetry={onBackToOptionSettings}
          />
        );
      default:
        throw new Error('unreachable code!');
    }
  };

  return <PaywallProvider>{renderContent()}</PaywallProvider>;
}
